import { useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { ProgressBar } from "primereact/progressbar";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { sanitizeTestId } from "sj-ui-toolkit/src/utils/string";
import { apiAssignmentPath } from "sj-ui-toolkit/src/constants/routes";
import { getRailsClient } from "sj-ui-toolkit/src/client";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";
import {
  loadAssignedUsersAsync,
  deleteAssignmentUsersAsync,
  setProcessingAssignment,
  isAssignmentProcessing,
} from "@/api/assignments";
import type { AssignedUser, DirectoryUserShape, FormValues, StoredUser } from "@/api/assignments";
import type { AssignmentReportRow } from "@/api/assignmentsReport";
import { loadAssignmentsReportRows } from "@/api/assignmentsReport";
import {
  ASSIGNMENT_STATUS_CONFIG,
  getAssignmentStatusKey,
} from "@/constants/assignmentsReport.constants";
import { statusBody, dateBody } from "@/helpers/tableBody";
import { useAvailableUsers } from "@/hooks/useAvailableUsers";
import AssignmentAudience from "@/components/assignments/AssignmentAudience";

// ---------------------------------------------------------------------------
// Types + column bodies
// ---------------------------------------------------------------------------

/** A single assigned user: membership (from the Rails endpoint) joined with report progress. */
interface AssignedUserRow {
  assignmentId?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  directoryUuid?: string;
  progress?: { passed: number; all: number } | null;
  completedAt?: string | null;
  status?: AssignmentReportRow["status"];
  timeSpent?: string | null;
}

const progressBody = (row: AssignedUserRow) => {
  const passed = row.progress?.passed ?? 0;
  const all = row.progress?.all ?? 0;
  const pct = all > 0 ? Math.round((passed / all) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <ProgressBar value={pct} style={{ height: "0.5rem", width: "8rem" }} showValue={false} />
      <span>{pct}%</span>
    </div>
  );
};

const assignmentStatusBody = statusBody<AssignedUserRow>(row => {
  const key = getAssignmentStatusKey(row);
  const config = ASSIGNMENT_STATUS_CONFIG[key];
  return {
    icon: config.icon || undefined,
    colorClass: config.color ? `text-${config.color}` : undefined,
    label: row.status ?? "Not started",
  };
});

const completedAtBody = dateBody<AssignedUserRow>("completedAt", "Incomplete");

// Columns the admin can show/hide via the column selector — identity columns (email/name)
// always stay visible.
const CONFIGURABLE_COLUMNS: { field: keyof AssignedUserRow; header: string }[] = [
  { field: "progress", header: "Progress" },
  { field: "status", header: "Status" },
  { field: "completedAt", header: "Completion Date" },
  { field: "timeSpent", header: "Time Spent" },
];

// Re-checks membership this often while newly-added users are still pending sync.
const POLL_INTERVAL_MS = 15000;

// A just-added row has no assignmentId until the server reconciles it — flag it as pending
// (not removable) so the disabled selection has a visible explanation. Each row gets its own
// uniquely-scoped Tooltip instance (rather than one shared by class) so only that tag's hover
// is ever targeted.
const emailBody = (row: AssignedUserRow) => {
  if (row.assignmentId) return <span>{row.email}</span>;

  const pendingId = `assignment-pending-${sanitizeTestId(row.email)}`;
  return (
    <div className="flex items-center gap-2">
      <span>{row.email}</span>
      <Tag id={pendingId} value="Pending" severity="warning" rounded />
      <Tooltip target={`#${pendingId}`} content="Processing request - please wait" position="top" />
    </div>
  );
};

/** Loads all report rows for an assignment (via the shared helper) into an email map. */
const loadReportRowsByEmail = async (
  assignmentName: string
): Promise<Map<string, AssignmentReportRow>> => {
  const rows = await loadAssignmentsReportRows(assignmentName);
  const map = new Map<string, AssignmentReportRow>();
  rows.forEach(row => {
    if (row.email) map.set(row.email.toLowerCase(), row);
  });
  return map;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AssignmentAudienceTabProps {
  assignmentId: string;
  assignmentName: string;
  /** Gates add/remove controls — read-only viewers still see progress. */
  canManage: boolean;
}

/** Assigned users + their assignment progress, with immediate add/remove management. */
export const AssignmentAudienceTab = ({
  assignmentId,
  assignmentName,
  canManage,
}: AssignmentAudienceTabProps) => {
  const { showToast } = useToast();
  const hideTimeSpent = useHideTimeSpent();
  const tenantName = getCurrentSelectedTenant() ?? "";
  const { availableUsers, allUsers, isLoading: isLoadingUsers } = useAvailableUsers(tenantName);
  const dt = useRef<DataTable<DataTableValueArray>>(null);

  const [rawAssigned, setRawAssigned] = useState<AssignedUser[]>([]);
  const [reportByEmail, setReportByEmail] = useState<Map<string, AssignmentReportRow>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Auto-resyncs membership after an add, until every row has a real assignmentId
  const [isPolling, setIsPolling] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [selection, setSelection] = useState<AssignedUserRow[]>([]);

  // Time Spent is hidden tenant-wide via a flag — drop it from the picker entirely rather
  // than letting an admin toggle a column that will never have data.
  const configurableColumns = useMemo(
    () => CONFIGURABLE_COLUMNS.filter(c => c.field !== "timeSpent" || !hideTimeSpent),
    [hideTimeSpent]
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    configurableColumns.map(c => c.field)
  );

  // Reconciles `visibleColumns` if `configurableColumns` changes after mount (e.g. the
  // hideTimeSpent flag resolves late).
  useEffect(() => {
    setVisibleColumns(prev => prev.filter(f => configurableColumns.some(c => c.field === f)));
  }, [configurableColumns]);

  const [addDialogVisible, setAddDialogVisible] = useState(false);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<DirectoryUserShape[]>([]);
  const [selectedUsersToRemove, setSelectedUsersToRemove] = useState<DirectoryUserShape[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string[]>([]);

  // showToast identity isn't stable, so keep it out of effect deps via a ref.
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  // Re-pulls membership + report data. Used after failures and by the manual Refresh
  // button — never shows the table skeleton, only (optionally) the button spinner.
  const refetch = async ({ showSpinner = false }: { showSpinner?: boolean } = {}) => {
    if (showSpinner) setIsRefreshing(true);
    try {
      const [assigned, reportMap] = await Promise.all([
        loadAssignedUsersAsync(assignmentId),
        loadReportRowsByEmail(assignmentName),
      ]);
      if (!mountedRef.current) return;
      setRawAssigned(assigned);
      setReportByEmail(reportMap);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error("Failed to refresh assigned users:", error);
      showToastRef.current({ severity: "error", detail: "Failed to refresh assigned users" });
    } finally {
      if (showSpinner && mountedRef.current) setIsRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    Promise.all([loadAssignedUsersAsync(assignmentId), loadReportRowsByEmail(assignmentName)])
      .then(([assigned, reportMap]) => {
        if (cancelled) return;
        setRawAssigned(assigned);
        setReportByEmail(reportMap);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("Failed to load assigned users:", error);
        showToastRef.current({ severity: "error", detail: "Failed to load assigned users" });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [assignmentId, assignmentName]);

  // While polling is active, silently re-pull membership every POLL_INTERVAL_MS until every
  // row has a real assignmentId or the 90s processing window closes.
  useEffect(() => {
    if (!isPolling) return;

    const interval = setInterval(() => {
      if (!isAssignmentProcessing(assignmentId)) {
        setIsPolling(false);
        return;
      }
      loadAssignedUsersAsync(assignmentId)
        .then(assigned => {
          if (!mountedRef.current) return;
          setRawAssigned(assigned);
          const stillPending = assigned.some(a => !a.id);
          if (!stillPending) setIsPolling(false);
        })
        .catch((error: unknown) => {
          console.error("Failed to poll assigned users:", error);
        });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isPolling, assignmentId]);

  // Directory users keyed by normalized email for O(1) lookups when building rows.
  const availableUsersByEmail = useMemo(() => {
    const map = new Map<string, DirectoryUserShape>();
    availableUsers.forEach(u => {
      if (u.email) map.set(u.email.toLowerCase(), u);
    });
    return map;
  }, [availableUsers]);

  const rows = useMemo<AssignedUserRow[]>(
    () =>
      rawAssigned
        .map((assignment): AssignedUserRow | null => {
          const apiUser = assignment.user;
          const email = apiUser?.email;
          if (!email) return null;
          const emailLower = email.toLowerCase();
          const report = reportByEmail.get(emailLower);
          const matched = availableUsersByEmail.get(emailLower);
          return {
            assignmentId: assignment.id,
            email,
            firstName: apiUser?.firstName ?? report?.firstName,
            lastName: apiUser?.lastName ?? report?.lastName,
            directoryUuid: apiUser?.directoryUuid ?? matched?.directoryUuid ?? email,
            progress: report?.progress ?? null,
            completedAt: report?.completedAt ?? null,
            status: report?.status ?? null,
            timeSpent: report?.timeSpent ?? null,
          };
        })
        .filter((r): r is AssignedUserRow => r !== null),
    [rawAssigned, reportByEmail, availableUsersByEmail]
  );

  // Assigned users in StoredUser shape so the add picker can exclude them.
  const assignedStoredUsers = useMemo<StoredUser[]>(
    () =>
      rows.map(r => ({
        assignmentId: r.assignmentId,
        directoryUuid: r.directoryUuid ?? r.email,
        email: r.email,
        firstName: r.firstName,
        lastName: r.lastName,
      })),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      r =>
        r.email.toLowerCase().includes(q) ||
        (r.firstName ?? "").toLowerCase().includes(q) ||
        (r.lastName ?? "").toLowerCase().includes(q)
    );
  }, [rows, searchText]);

  const handleRemove = async () => {
    const ids = selection.map(r => r.assignmentId).filter((id): id is string => Boolean(id));
    if (ids.length === 0) return;

    const removedEmails = new Set(selection.map(r => r.email.toLowerCase()));
    // Optimistic removal so the table reflects the change immediately.
    setRawAssigned(prev =>
      prev.filter(a => !removedEmails.has(a.user?.email?.toLowerCase() ?? ""))
    );
    setSelection([]);

    try {
      await deleteAssignmentUsersAsync(assignmentId, ids);
      setProcessingAssignment(assignmentId);
      showToastRef.current({
        severity: "success",
        detail: `Removed ${ids.length} user${ids.length === 1 ? "" : "s"}`,
      });
    } catch (error) {
      console.error("Failed to remove users:", error);
      showToastRef.current({ severity: "error", detail: "Failed to remove users" });
      // Only reconcile with the server on failure — success stays optimistic (no skeleton).
      void refetch();
    }
  };

  const handleAddChange = async (next: FormValues["target"]) => {
    const nextUsers = next.users ?? [];
    const added = nextUsers.filter(
      u => !assignedStoredUsers.some(a => a.directoryUuid === u.directoryUuid)
    );

    if (added.length === 0) {
      setAddDialogVisible(false);
      return;
    }

    const userEmails = added
      .map(u => u.email)
      .filter((email): email is string => Boolean(email))
      .join(",");
    const addedEmails = new Set(
      added.map(u => u.email?.toLowerCase()).filter((e): e is string => Boolean(e))
    );

    // Optimistically append so the new users show immediately. The report svc lags, so
    // re-fetching here would drop them until it catches up — use Refresh to reconcile.
    setRawAssigned(prev => [
      ...prev,
      ...added.map(u => ({
        user: {
          email: u.email,
          firstName: u.firstName,
          lastName: u.lastName,
          directoryUuid: u.directoryUuid,
        },
      })),
    ]);
    setAddDialogVisible(false);
    setSelectedUsersToAdd([]);

    try {
      await getRailsClient().post(apiAssignmentPath(assignmentId), { userEmails });
      setProcessingAssignment(assignmentId);
      showToastRef.current({
        severity: "success",
        detail: `Added ${added.length} user${added.length === 1 ? "" : "s"}`,
      });
      // Auto-resync until the newly added rows pick up a real assignmentId.
      setIsPolling(true);
    } catch (error) {
      console.error("Failed to add users:", error);
      showToastRef.current({ severity: "error", detail: "Failed to add users" });
      // Roll back the optimistic append.
      setRawAssigned(prev =>
        prev.filter(a => !addedEmails.has(a.user?.email?.toLowerCase() ?? ""))
      );
    }
  };

  const addTarget: FormValues["target"] = useMemo(
    () => ({ users: assignedStoredUsers, availableUsers, allUsers }),
    [assignedStoredUsers, availableUsers, allUsers]
  );

  if (isLoading || isLoadingUsers) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton width="16rem" height="2.5rem" />
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height="2.5rem" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <IconField iconPosition="left" className="max-w-72">
          <InputIcon className="pi pi-search" />
          <InputText
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search users..."
            aria-label="Search assigned users"
            data-testid="assignment-assigned-users-search-input"
            className="w-full"
          />
        </IconField>

        <div className="flex items-center gap-2">
          <MultiSelect
            value={visibleColumns}
            onChange={e => setVisibleColumns(e.value as string[])}
            options={configurableColumns}
            optionLabel="header"
            optionValue="field"
            placeholder="Select Columns"
            aria-label="Select visible columns"
            className="w-48"
            maxSelectedLabels={2}
            data-testid="assignment-assigned-users-column-select"
          />
          <Button
            label="Export"
            icon="pi pi-download"
            iconPos="left"
            text
            data-testid="assignment-assigned-users-export-button"
            onClick={() => dt.current?.exportCSV({ selectionOnly: false })}
            tooltip="Export data as CSV"
            tooltipOptions={{ position: "top" }}
          />
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            text
            loading={isRefreshing}
            data-testid="assignment-assigned-users-refresh-button"
            onClick={() => void refetch({ showSpinner: true })}
          />
          {canManage && selection.length > 0 && (
            <Button
              label={`Remove ${selection.length}`}
              icon="pi pi-trash"
              severity="danger"
              outlined
              data-testid="assignment-assigned-users-remove-button"
              onClick={handleRemove}
            />
          )}
          {canManage && (
            <Button
              label="Add Users"
              icon="pi pi-plus"
              data-testid="assignment-assigned-users-add-button"
              onClick={() => setAddDialogVisible(true)}
            />
          )}
        </div>
      </div>

      <DataTable
        ref={dt}
        value={filteredRows}
        dataKey="email"
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50, 100]}
        data-testid="assignment-assigned-users-table"
        removableSort
        selectionMode="multiple"
        isDataSelectable={e => canManage && Boolean((e.data as AssignedUserRow).assignmentId)}
        selection={selection}
        onSelectionChange={e => {
          // Selection is only actionable for managers; ignore clicks for read-only viewers.
          if (canManage) setSelection(e.value as AssignedUserRow[]);
        }}
        emptyMessage="No users assigned yet."
        tableClassName="dt-responsive-text"
        exportFunction={({ data, field, rowData }) => {
          const row = rowData as AssignedUserRow;
          if (field === "progress") {
            return `${row.progress?.passed ?? 0}/${row.progress?.all ?? 0}`;
          }
          if (field === "status") {
            return row.status ?? "Not started";
          }
          if (field === "completedAt") {
            return row.completedAt ?? "Incomplete";
          }
          return String(data ?? "");
        }}
      >
        {canManage && (
          <Column
            selectionMode="multiple"
            headerStyle={{ width: "3rem" }}
            pt={{
              rowCheckbox: { root: { "data-testid": "assignment-assigned-users-row-checkbox" } },
            }}
          />
        )}
        <Column field="email" header="Email" sortable style={{ width: "280px" }} body={emailBody} />
        <Column field="firstName" header="First" sortable body={r => r.firstName ?? "--"} />
        <Column field="lastName" header="Last" sortable body={r => r.lastName ?? "--"} />
        {visibleColumns.includes("progress") && (
          <Column
            field="progress"
            header="Progress"
            body={progressBody}
            style={{ width: "180px" }}
          />
        )}
        {visibleColumns.includes("status") && (
          <Column field="status" header="Status" sortable body={assignmentStatusBody} />
        )}
        {visibleColumns.includes("completedAt") && (
          <Column
            field="completedAt"
            header="Completion Date"
            style={{ width: "180px" }}
            body={completedAtBody}
          />
        )}
        {!hideTimeSpent && visibleColumns.includes("timeSpent") && (
          <Column
            field="timeSpent"
            header="Time Spent"
            style={{ width: "130px" }}
            body={r => r.timeSpent ?? "\u2014"}
          />
        )}
      </DataTable>

      {canManage && (
        <Dialog
          header="Add Users"
          visible={addDialogVisible}
          onHide={() => setAddDialogVisible(false)}
          style={{ width: "75rem" }}
          breakpoints={{ "1200px": "95vw" }}
          maximizable
          dismissableMask
          data-testid="assignment-add-users-dialog"
        >
          <AssignmentAudience
            values={addTarget}
            handleChange={handleAddChange}
            selectedUsersToAdd={selectedUsersToAdd}
            setSelectedUsersToAdd={setSelectedUsersToAdd}
            selectedUsersToRemove={selectedUsersToRemove}
            setSelectedUsersToRemove={setSelectedUsersToRemove}
            selectedLevel={selectedLevel}
            onLevelChange={setSelectedLevel}
            showNewHire={false}
            availableOnly
            excludeFields={["attributes.country"]}
          />
        </Dialog>
      )}
    </div>
  );
};

export default AssignmentAudienceTab;
